import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import SearchPanel from '@components/SearchPanel';
import I18n from '@utils/i18n';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import api from '@utils/axios';
import useDebounce from '@hooks/useDebounce';
import { datatableProposalsPath, updateStatusProposalPath } from 'routes';
import { saveCacheToLocalStorage } from '@helpers/localStorage';
import kendoDatatableParams from '@helpers/kendoDatatableParams';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import ColumnMenu from './nested/ColumnMenu';
import ColumnMenuContext from './nested/ColumnMenuContext';
import { getInitialData, getInitialFilters } from './helpers/initialData';
import './index.css';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import { actionTypes, reducer } from './reducer';
import {
  ALL_COLUMNS as ALL_COLUMNS_FROM_CONSTANTS,
  ALL_COLUMNS_DATA,
  CACHE_KEY as DEFAULT_CACHE_KEY
} from './constants';
import CustomScrollbar from '../../../components/CustomScrollbar/CustomScrollbar';
import MassActions from './nested/MassActions';
import useFilters from './hooks/useFilters';
import StatusChangeContext from './nested/StatusChangeContext';

function Index({
  statusesForSelect,
  usersForSelect,
  techniciansForSelect,
  proposalTypesForSelect,
  hasShowAllProposalsInCustomerPortal,
  hideExport,
  buildingId,
  accountId,
  inspectionId,
  cacheKey
}) {
  const ALL_COLUMNS = useMemo(
    () => (buildingId ? ALL_COLUMNS_FROM_CONSTANTS.filter((c) => c !== 'building') : ALL_COLUMNS_FROM_CONSTANTS),
    [buildingId]
  );

  const initialData = useMemo(
    () => getInitialData({ cacheKey, buildingId, accountId, inspectionId }),
    [cacheKey, buildingId, accountId, inspectionId]
  );

  const [state, dispatch] = useReducer(reducer, {
    data: [],
    totalCount: 0,
    ...initialData,
    columns: buildingId ? initialData.columns.filter((c) => c !== 'building') : initialData.columns
  });

  const { page, sort, filters, columns, data, totalCount, loading, selectAll, search } = state;

  // Publish live grid state to the AI chat widget (page context overlay).
  usePublishListContext({ page, sort, filters, totalCount, data, search });

  const debouncedFilters = useDebounce(filters);

  const filtersList = useFilters({
    filters,
    dispatch,
    statusesForSelect,
    usersForSelect,
    techniciansForSelect,
    proposalTypesForSelect,
    buildingId,
    accountId,
    inspectionId
  });

  useEffect(() => {
    saveCacheToLocalStorage(cacheKey, { page, sort, filters, columns });
  }, [page, sort, filters, columns, cacheKey]);

  const debouncedSearchChange = useMemo(
    () =>
      debounce((value) => {
        dispatch({ type: actionTypes.SEARCH_CHANGED, search: value });
      }, 300),
    []
  );

  const onSearchChange = (value) => debouncedSearchChange(value);

  useEffect(() => {
    if (filters !== debouncedFilters) return undefined;

    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    const controller = new AbortController();

    api
      .get(datatableProposalsPath(), {
        params: kendoDatatableParams({ page, sort, filters: debouncedFilters, search }),
        signal: controller.signal
      })
      .then(({ data: proposalsData }) => dispatch({ type: actionTypes.DATA_LOADED, data: proposalsData }))
      .catch((err) => {
        if (axios.isCancel(err)) return;
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: false });
      });

    return () => controller.abort();
  }, [page, sort, filters, debouncedFilters, search]);

  const onSelectionChange = (event) => {
    dispatch({ type: actionTypes.SELECTION_CHANGED, id: event.dataItem.id });
  };

  const onHeaderSelectionChange = (event) => {
    dispatch({ type: actionTypes.HEADER_SELECTION_CHANGED, checked: event.syntheticEvent.target.checked });
  };

  const onSelectAll = () => {
    dispatch({ type: actionTypes.ALL_SELECTED });
  };

  // Mass actions resolve the selected ids server-side from these params when the user picks
  // "Select all N proposals entirely", so they must narrow the data the same way the grid's
  // own datatable request does — filters AND the search term (IP-14746).
  const massActionQueryParams = useMemo(() => {
    return kendoDatatableParams({ filters, search });
  }, [filters, search]);

  const onColumnsChange = (cols) => {
    dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: cols.map((col) => col.id) });
  };

  const onResetFilters = () => {
    dispatch({ type: actionTypes.FILTER_CHANGED, filters: getInitialFilters(buildingId, accountId, inspectionId) });
  };

  const onResetCache = () => {
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;
    localStorage.removeItem(cacheKey);
    window.location.reload();
  };

  const getScreenInfo = () => {
    if (buildingId) return { screen: 'building', screenId: buildingId };
    if (accountId) return { screen: 'account', screenId: accountId };
    if (inspectionId) return { screen: 'inspection', screenId: inspectionId };
    return { screen: null, screenId: null };
  };

  const allMenuColumns = useMemo(() => ALL_COLUMNS.map((column) => ALL_COLUMNS_DATA[column]), [ALL_COLUMNS]);
  const selectedMenuColumns = useMemo(() => columns.map((column) => ALL_COLUMNS_DATA[column]), [columns]);
  const visibleColumns = useMemo(() => ['selected', ...columns].map((column) => ALL_COLUMNS_DATA[column]), [columns]);

  const headerSelectionValue = selectAll || (totalCount > 0 && data.every((proposal) => proposal.selected));
  // IP-15353: persist a status change made from the row's Actions menu, then repaint
  // just that row from the server's response rather than refetching the page — the
  // row does not move, and a refetch would throw away the user's scroll position.
  const onChangeStatus = useCallback(async (proposalId, proposalStatusId) => {
    const { data: updated } = await api.patch(
      updateStatusProposalPath({ id: proposalId, format: 'json', _options: true }),
      { proposal_status_id: proposalStatusId }
    );

    dispatch({ type: actionTypes.ROW_STATUS_UPDATED, proposal: updated });
  }, []);

  const statusChangeContextValue = useMemo(
    () => ({ statuses: statusesForSelect, onChangeStatus }),
    [statusesForSelect, onChangeStatus]
  );

  const columnContextValue = useMemo(
    () => ({
      onColumnsChange,
      columnsState: selectedMenuColumns,
      columns: allMenuColumns,
      ...getScreenInfo()
    }),
    [selectedMenuColumns, allMenuColumns, onColumnsChange, buildingId, accountId, inspectionId]
  );

  return (
    <KendoLocalizationProvider>
      <ColumnMenuContext.Provider value={columnContextValue}>
        <StatusChangeContext.Provider value={statusChangeContextValue}>
          <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList}>
            <div className="filters__group--search">
              <SearchPanel placeholder={I18n.t('filters.search')} onChange={onSearchChange} />
            </div>
          </FilterPanel>
          {loading && (
            <div className="workspace__section">
              <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
                <svg role="img">
                  <use href="/map.svg#load-spinner" />
                </svg>
              </div>
            </div>
          )}
          {!loading && (
            <CustomScrollbar>
              <Grid
                className="qmb-grid--01"
                data={data}
                total={totalCount}
                skip={page.skip}
                take={page.take}
                onPageChange={(event) => {
                  dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
                }}
                pageable={{
                  type: 'input',
                  pageSizes: [25, 50, 100],
                  pageSizeValue: page.take
                }}
                pager={EnterKeyPager}
                sortable
                sort={sort}
                onSortChange={(event) => {
                  dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
                }}
                dataItemKey="id"
                selectedField="selected"
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}>
                <GridToolbar>
                  <MassActions
                    state={state}
                    onSelectAll={onSelectAll}
                    queryParams={massActionQueryParams}
                    hideExport={hideExport}
                    hasShowAllProposalsInCustomerPortal={hasShowAllProposalsInCustomerPortal}
                  />
                </GridToolbar>
                {visibleColumns.map((column) => (
                  <Column
                    key={column.id}
                    field={column.field}
                    title={column.title}
                    columnMenu={ColumnMenu}
                    cell={column.cell}
                    sortable={column.sortable}
                    width={column.width || 150}
                    format={column.format}
                    headerSelectionValue={headerSelectionValue}
                    className={column.className}
                    headerClassName={column.headerClassName}
                  />
                ))}
              </Grid>
            </CustomScrollbar>
          )}
        </StatusChangeContext.Provider>
      </ColumnMenuContext.Provider>
    </KendoLocalizationProvider>
  );
}

const selectShape = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })
);

Index.propTypes = {
  statusesForSelect: selectShape,
  usersForSelect: selectShape,
  techniciansForSelect: selectShape,
  proposalTypesForSelect: selectShape,
  hasShowAllProposalsInCustomerPortal: PropTypes.bool.isRequired,
  hideExport: PropTypes.bool.isRequired,
  buildingId: PropTypes.number,
  accountId: PropTypes.number,
  inspectionId: PropTypes.number,
  cacheKey: PropTypes.string
};

Index.defaultProps = {
  statusesForSelect: [],
  usersForSelect: [],
  techniciansForSelect: [],
  proposalTypesForSelect: [],
  buildingId: null,
  accountId: null,
  inspectionId: null,
  cacheKey: DEFAULT_CACHE_KEY
};

export default Index;
